import type { Id, Scene, StatusStepDef } from "@supermanus/document-model";
import {
  createId,
  episodeLevelStatusProgress,
  formatDuration,
  getEpisodeStatusSteps,
  getSceneStatusSteps,
  isStatusStepSpacer,
  sceneStatusProgress,
  totalSceneDuration,
} from "@supermanus/document-model";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { DocumentHead } from "./DocumentHead";
import { handleFieldEnterKey } from "./fieldKeyboard";
import { sceneHeaderBg } from "./SceneHeaderColorMenu";
import { EpisodeStatusStats } from "./StatusProgressBar";
import {
  reorderByBefore,
  usePointerReorder,
} from "./usePointerReorder";

export interface StatusViewProps {
  scenes: Scene[];
  readOnly?: boolean;
  productionTitle: string;
  seasonTitle?: string | null;
  episodeTitle: string;
  defaultSceneHeaderBg: string;
  statusSteps: StatusStepDef[];
  /** Episode-level checklist (empty = scene points only). */
  episodeStatusSteps?: StatusStepDef[];
  episodeStatusCompletedIds?: Id[];
  onToggleStep: (sceneId: Id, stepId: Id) => void;
  onToggleEpisodeStep?: (stepId: Id) => void;
  /** Update the production default list. Does not overwrite per-scene lists. */
  onChangeProductionSteps: (steps: StatusStepDef[]) => void;
  /** Set steps for one scene (`null` → inherit production defaults). */
  onChangeSceneSteps: (sceneId: Id, steps: StatusStepDef[] | null) => void;
  onChangeEpisodeSteps?: (steps: StatusStepDef[]) => void;
  showHours?: boolean;
  showFrames?: boolean;
  fps?: number;
  /** Extra class on each scene block (e.g. director own/program). */
  sceneRowClass?: (sceneId: Id) => string | undefined;
}

function withSortOrder(steps: StatusStepDef[]): StatusStepDef[] {
  return steps.map((s, i) => ({ ...s, sortOrder: i }));
}

/** True when labels cannot fit horizontally—even with wrapping—in equal-width slots. */
function measureNeedsVerticalLabels(bar: HTMLElement): boolean {
  const steps = [...bar.querySelectorAll<HTMLElement>(".sm-status-step")];
  if (steps.length === 0) return false;

  const styles = getComputedStyle(bar);
  const gap = parseFloat(styles.columnGap || styles.gap) || 0;
  const available = bar.clientWidth - gap * (steps.length - 1);
  if (available <= 0) return true;
  const slot = available / steps.length;

  const probe = document.createElement("span");
  probe.setAttribute("aria-hidden", "true");
  document.body.appendChild(probe);

  const maxWrappedLines = 3;

  try {
    for (const step of steps) {
      const label = step.querySelector<HTMLElement>(".sm-status-step-label");
      if (!label) continue;
      const labelStyles = getComputedStyle(label);
      const fontSize = parseFloat(labelStyles.fontSize) || 10;
      const lineHeight =
        parseFloat(labelStyles.lineHeight) || fontSize * 1.2;
      const padX =
        (parseFloat(getComputedStyle(step).paddingLeft) || 0) +
        (parseFloat(getComputedStyle(step).paddingRight) || 0);
      const contentWidth = Math.max(0, slot - padX);
      const text = (label.textContent ?? "").trim();
      if (!text) continue;

      if (contentWidth < fontSize * 1.75) return true;

      const font = labelStyles.font;
      const letterSpacing = labelStyles.letterSpacing;

      probe.style.cssText =
        "position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap;pointer-events:none;writing-mode:horizontal-tb;";
      probe.style.font = font;
      probe.style.letterSpacing = letterSpacing;
      const words = text.split(/\s+/).filter(Boolean);
      for (const word of words) {
        probe.textContent = word;
        if (probe.offsetWidth > contentWidth + 0.5) return true;
      }

      probe.style.cssText = [
        "position:absolute",
        "left:-9999px",
        "top:0",
        "visibility:hidden",
        "pointer-events:none",
        "writing-mode:horizontal-tb",
        "white-space:normal",
        "overflow-wrap:break-word",
        "word-break:normal",
        `width:${contentWidth}px`,
      ].join(";");
      probe.style.font = font;
      probe.style.letterSpacing = letterSpacing;
      probe.style.lineHeight = String(lineHeight);
      probe.textContent = text;
      if (probe.offsetHeight > lineHeight * maxWrappedLines + 1) return true;
    }
  } finally {
    probe.remove();
  }
  return false;
}

function pickStatusMeasureSceneId(
  scenes: Scene[],
  statusSteps: StatusStepDef[],
): Id | null {
  let best: Id | null = null;
  let bestScore = -1;
  for (const scene of scenes) {
    const steps = getSceneStatusSteps(scene, statusSteps);
    const score =
      steps.length * 1000 + steps.reduce((n, s) => n + s.label.length, 0);
    if (score > bestScore) {
      bestScore = score;
      best = scene.id;
    }
  }
  return best;
}

function useCompactStatusLabels(
  sampleBarRef: RefObject<HTMLElement | null>,
  stepSignature: string,
): boolean {
  const [compact, setCompact] = useState(false);

  useLayoutEffect(() => {
    const bar = sampleBarRef.current;
    if (!bar) {
      setCompact(false);
      return;
    }

    const measure = () => {
      const needs = measureNeedsVerticalLabels(bar);
      setCompact((prev) => (prev === needs ? prev : needs));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [sampleBarRef, stepSignature]);

  return compact;
}

function StepDraftInput({
  step,
  index,
  disabled,
  onCommit,
}: {
  step: StatusStepDef;
  index: number;
  disabled?: boolean;
  onCommit: (stepId: Id, label: string) => void;
}) {
  const [draft, setDraft] = useState(step.label);
  useEffect(() => {
    setDraft(step.label);
  }, [step.id, step.label]);

  return (
    <input
      value={draft}
      size={Math.max(1, Array.from(draft).length)}
      disabled={disabled}
      aria-label={`Punkt ${index + 1}`}
      placeholder="Navn eller tomt felt"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== step.label) onCommit(step.id, draft);
      }}
      onKeyDown={handleFieldEnterKey}
    />
  );
}

function StepListEditor({
  steps,
  readOnly,
  onChange,
}: {
  steps: StatusStepDef[];
  readOnly?: boolean;
  onChange: (next: StatusStepDef[]) => void;
}) {
  const { listRef, dragId, dropBeforeId, beginDrag } = usePointerReorder({
    readOnly,
    axis: "y",
    onMove: (id, beforeId) => {
      onChange(withSortOrder(reorderByBefore(steps, id, beforeId)));
    },
  });

  function rename(stepId: Id, label: string) {
    onChange(
      withSortOrder(
        steps.map((s) => (s.id === stepId ? { ...s, label } : s)),
      ),
    );
  }

  function remove(stepId: Id) {
    onChange(withSortOrder(steps.filter((s) => s.id !== stepId)));
  }

  function add() {
    onChange(
      withSortOrder([
        ...steps,
        { id: createId(), label: "", sortOrder: steps.length },
      ]),
    );
  }

  return (
    <div className="sm-status-step-editor">
      <ul
        ref={listRef as RefObject<HTMLUListElement>}
        className={`sm-status-step-list${dragId ? " is-reordering" : ""}${
          dragId && dropBeforeId === null ? " is-drop-end" : ""
        }`}
      >
        {steps.map((step, i) => (
          <li
            key={step.id}
            data-step-id={step.id}
            className={
              dragId === step.id
                ? "is-dragging"
                : dropBeforeId === step.id
                  ? "is-drop-before"
                  : undefined
            }
          >
            <button
              type="button"
              className="sm-status-step-grip"
              disabled={readOnly}
              aria-label="Flyt punkt"
              title="Træk for at flytte"
              onPointerDown={(e: ReactPointerEvent<HTMLElement>) =>
                beginDrag(step.id, e)
              }
            >
              ⋮⋮
            </button>
            <StepDraftInput
              step={step}
              index={i}
              disabled={readOnly}
              onCommit={rename}
            />
            <button
              type="button"
              disabled={readOnly}
              onClick={() => remove(step.id)}
            >
              Fjern
            </button>
          </li>
        ))}
      </ul>
      {!readOnly ? (
        <button type="button" onClick={add}>
          + Tilføj punkt
        </button>
      ) : null}
    </div>
  );
}

function SceneStepBar({
  scene,
  steps,
  compactLabels,
  readOnly,
  barRef,
  onToggle,
  onReorder,
}: {
  scene: Scene;
  steps: StatusStepDef[];
  compactLabels: boolean;
  readOnly?: boolean;
  barRef?: RefObject<HTMLDivElement | null>;
  onToggle: (stepId: Id) => void;
  onReorder: (next: StatusStepDef[]) => void;
}) {
  const done = new Set(scene.statusCompletedIds ?? []);
  const { listRef, dragId, dropBeforeId, beginDrag } = usePointerReorder({
    readOnly,
    axis: "x",
    onMove: (id, beforeId) => {
      onReorder(withSortOrder(reorderByBefore(steps, id, beforeId)));
    },
    onActivate: (id) => {
      const step = steps.find((s) => s.id === id);
      if (!step || isStatusStepSpacer(step)) return;
      onToggle(id);
    },
  });

  return (
    <div
      ref={(el) => {
        listRef.current = el;
        if (barRef) barRef.current = el;
      }}
      className={`sm-status-bar${compactLabels ? " is-compact" : ""}${
        dragId ? " is-reordering" : ""
      }${dragId && dropBeforeId === null ? " is-drop-end" : ""}`}
      role="group"
      aria-label={`Status for scene ${scene.number}`}
    >
      {steps.map((step) => {
        const checked = done.has(step.id);
        const blank = isStatusStepSpacer(step);
        return (
          <button
            key={step.id}
            type="button"
            data-step-id={step.id}
            className={`sm-status-step${checked ? " is-done" : ""}${
              blank ? " is-spacer" : ""
            }${dragId === step.id ? " is-dragging" : ""}${
              dropBeforeId === step.id ? " is-drop-before" : ""
            }`}
            disabled={readOnly}
            aria-pressed={blank ? undefined : checked}
            title={
              blank
                ? "Træk for at flytte"
                : `${step.label} — klik for at sætte, træk for at flytte`
            }
            onPointerDown={(e) => beginDrag(step.id, e)}
          >
            <span className="sm-status-step-mark">
              {blank ? "" : checked ? "✓" : ""}
            </span>
            <span className="sm-status-step-label">{step.label}</span>
          </button>
        );
      })}
    </div>
  );
}

const EPISODE_BAR_GAP_PX = 4; // matches .sm-status-bar gap: 0.25rem

/** Min width when label is rotated (vertical) — just enough for glyphs. */
function episodeCustomRotatedMinWidthPx(): number {
  return 26;
}

/** Min width to show the label horizontally (wrapping longest word). */
function episodeCustomHorizontalMinWidthPx(label: string): number {
  const text = label.trim() || "·";
  const longest = text.split(/\s+/).reduce((m, w) => Math.max(m, w.length), 1);
  return Math.max(36, Math.ceil(longest * 6.2 + 12));
}

export type EpisodeRollupLayout = {
  fuldførte: number | null;
  customs: number[];
  manglende: number | null;
  /** Per custom chip (incl. spacers): true = horizontal label. */
  customHorizontal: boolean[];
};

/**
 * Pixel widths so Σ(done chips) / Σ(all chips) === progress pct.
 * Every real episode point is the same width (1/T). Labels rotate unless that
 * shared width is enough for horizontal text.
 */
function layoutEpisodeRollupWidths(opts: {
  barWidth: number;
  sceneCompleted: number;
  sceneMissing: number;
  customs: { label: string; done: boolean; blank: boolean }[];
}): EpisodeRollupLayout {
  const sc = Math.max(0, opts.sceneCompleted);
  const sm = Math.max(0, opts.sceneMissing);
  const showF = sc > 0;
  const showM = sm > 0;
  const real = opts.customs.map((c, i) => ({ ...c, i })).filter((c) => !c.blank);
  const ct = real.length;
  const cd = real.filter((c) => c.done).length;
  const T = sc + sm + ct;

  const chipCount =
    (showF ? 1 : 0) + opts.customs.length + (showM ? 1 : 0);
  const gaps = Math.max(0, chipCount - 1) * EPISODE_BAR_GAP_PX;
  const avail = Math.max(0, opts.barWidth - gaps);

  const customsOut = opts.customs.map((c) =>
    c.blank ? Math.min(12, Math.max(6, avail * 0.02)) : 0,
  );
  const customHorizontal = opts.customs.map(() => false);
  const spacerTotal = customsOut.reduce((a, w, i) => {
    return opts.customs[i]?.blank ? a + w : a;
  }, 0);
  const pointAvail = Math.max(0, avail - spacerTotal);

  if (T <= 0 || pointAvail <= 0) {
    return {
      fuldførte: showF ? 0 : null,
      customs: customsOut,
      manglende: showM ? 0 : null,
      customHorizontal,
    };
  }

  const p = (sc + cd) / T;
  const unit = pointAvail / T;
  const rotatedMin = episodeCustomRotatedMinWidthPx();

  function rollupsForShared(shared: number): { ok: boolean; fW: number; mW: number } {
    const doneW = cd * shared;
    const allCustomW = ct * shared;
    const fW = p * pointAvail - doneW;
    const mW = (1 - p) * pointAvail - (allCustomW - doneW);
    const ok =
      (!showF || fW >= -0.51) &&
      (!showM || mW >= -0.51) &&
      (showF || Math.abs(fW) <= 0.51) &&
      (showM || Math.abs(mW) <= 0.51);
    return { ok, fW, mW };
  }

  // Prefer pure 1/T; grow all customs equally only if needed for rotated text.
  let shared = unit;
  if (ct > 0 && unit + 0.5 < rotatedMin) {
    const desired = rotatedMin;
    if (rollupsForShared(desired).ok) {
      shared = desired;
    } else {
      let lo = unit;
      let hi = desired;
      for (let i = 0; i < 16; i++) {
        const mid = (lo + hi) / 2;
        if (rollupsForShared(mid).ok) lo = mid;
        else hi = mid;
      }
      shared = lo;
    }
  }

  const { fW, mW } = rollupsForShared(shared);
  real.forEach((c) => {
    customsOut[c.i] = shared;
    customHorizontal[c.i] =
      shared + 0.5 >= episodeCustomHorizontalMinWidthPx(c.label);
  });

  return {
    fuldførte: showF ? Math.max(0, fW) : null,
    customs: customsOut,
    manglende: showM ? Math.max(0, mW) : null,
    customHorizontal,
  };
}

function useEpisodeRollupWidths(
  barRef: RefObject<HTMLElement | null>,
  sceneCompleted: number,
  sceneMissing: number,
  customs: { label: string; done: boolean; blank: boolean }[],
): EpisodeRollupLayout {
  const [barWidth, setBarWidth] = useState(0);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const measure = () => setBarWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [barRef, customs.length, sceneCompleted, sceneMissing]);

  const widths = useMemo(() => {
    const key = customs
      .map((c) => `${c.blank ? 0 : 1}:${c.done ? 1 : 0}:${c.label}`)
      .join("|");
    void key;
    return layoutEpisodeRollupWidths({
      barWidth: barWidth > 0 ? barWidth : 480,
      sceneCompleted,
      sceneMissing,
      customs,
    });
  }, [barWidth, sceneCompleted, sceneMissing, customs]);

  return widths;
}

function EpisodeRollupBar({
  sceneCompleted,
  sceneMissing,
  steps,
  completedIds,
  readOnly,
  barRef,
  onToggle,
  onReorder,
}: {
  sceneCompleted: number;
  sceneMissing: number;
  steps: StatusStepDef[];
  completedIds: Id[];
  readOnly?: boolean;
  barRef?: RefObject<HTMLDivElement | null>;
  onToggle: (stepId: Id) => void;
  onReorder: (next: StatusStepDef[]) => void;
}) {
  const localRef = useRef<HTMLDivElement | null>(null);
  const customs = useMemo(() => {
    const doneSet = new Set(completedIds);
    return steps.map((s) => ({
      label: s.label,
      done: doneSet.has(s.id),
      blank: isStatusStepSpacer(s),
    }));
  }, [steps, completedIds]);

  const setRefs = (el: HTMLDivElement | null) => {
    localRef.current = el;
    if (barRef) barRef.current = el;
  };

  const widths = useEpisodeRollupWidths(
    localRef,
    sceneCompleted,
    sceneMissing,
    customs,
  );

  const done = useMemo(() => new Set(completedIds), [completedIds]);

  const { listRef, dragId, dropBeforeId, beginDrag } = usePointerReorder({
    readOnly,
    axis: "x",
    onMove: (id, beforeId) => {
      const before =
        beforeId && steps.some((s) => s.id === beforeId) ? beforeId : null;
      onReorder(withSortOrder(reorderByBefore(steps, id, before)));
    },
    onActivate: (id) => {
      const step = steps.find((s) => s.id === id);
      if (!step || isStatusStepSpacer(step)) return;
      onToggle(id);
    },
  });

  return (
    <div
      ref={(el) => {
        listRef.current = el;
        setRefs(el);
      }}
      className={`sm-status-bar sm-status-bar-episode${
        dragId ? " is-reordering" : ""
      }${dragId && dropBeforeId === null ? " is-drop-end" : ""}`}
      role="group"
      aria-label="Status for hele episoden"
    >
      {widths.fuldførte != null ? (
        <div
          className="sm-status-step is-rollup is-done"
          style={{
            flex: `0 0 ${Math.max(0, widths.fuldførte)}px`,
            width: Math.max(0, widths.fuldførte),
            maxWidth: Math.max(0, widths.fuldførte),
          }}
          title={`${sceneCompleted} fuldførte scenepunkter`}
          aria-label={`${sceneCompleted} fuldførte scenepunkter`}
        >
          <span className="sm-status-step-mark">✓</span>
          <span className="sm-status-step-label">Fuldførte scenepunkter</span>
          <span className="sm-status-step-count">{sceneCompleted}</span>
        </div>
      ) : null}

      {steps.map((step, index) => {
        const checked = done.has(step.id);
        const blank = isStatusStepSpacer(step);
        const w = widths.customs[index] ?? 0;
        const horizontal = blank || !!widths.customHorizontal[index];
        return (
          <button
            key={step.id}
            type="button"
            data-step-id={step.id}
            className={`sm-status-step${checked ? " is-done" : ""}${
              blank ? " is-spacer" : ""
            }${horizontal ? "" : " is-label-vertical"}${
              dragId === step.id ? " is-dragging" : ""
            }${dropBeforeId === step.id ? " is-drop-before" : ""}`}
            style={{
              flex: `0 0 ${w}px`,
              width: w,
              maxWidth: w,
            }}
            disabled={readOnly}
            aria-pressed={blank ? undefined : checked}
            title={
              blank
                ? "Træk for at flytte"
                : `${step.label} — klik for at sætte, træk for at flytte`
            }
            onPointerDown={(e) => beginDrag(step.id, e)}
          >
            <span className="sm-status-step-mark">
              {blank ? "" : checked ? "✓" : ""}
            </span>
            <span className="sm-status-step-label">{step.label}</span>
          </button>
        );
      })}

      {widths.manglende != null ? (
        <div
          className="sm-status-step is-rollup is-missing"
          style={{
            flex: `0 0 ${Math.max(0, widths.manglende)}px`,
            width: Math.max(0, widths.manglende),
            maxWidth: Math.max(0, widths.manglende),
          }}
          title={`${sceneMissing} manglende scenepunkter`}
          aria-label={`${sceneMissing} manglende scenepunkter`}
        >
          <span className="sm-status-step-mark" />
          <span className="sm-status-step-label">Manglende scenepunkter</span>
          <span className="sm-status-step-count">{sceneMissing}</span>
        </div>
      ) : null}
    </div>
  );
}

const EPISODE_EXPAND_ID = "__episode__" as Id;

export function StatusView({
  scenes,
  readOnly,
  productionTitle,
  seasonTitle,
  episodeTitle,
  defaultSceneHeaderBg,
  statusSteps,
  episodeStatusSteps = [],
  episodeStatusCompletedIds = [],
  onToggleStep,
  onToggleEpisodeStep,
  onChangeProductionSteps,
  onChangeSceneSteps,
  onChangeEpisodeSteps,
  showHours = true,
  showFrames = false,
  fps = 25,
  sceneRowClass,
}: StatusViewProps) {
  const sampleBarRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState<Set<Id>>(() => new Set());
  const [editing, setEditing] = useState<Id | "all" | "episode" | null>(null);

  const productionList = useMemo(
    () => [...statusSteps].sort((a, b) => a.sortOrder - b.sortOrder),
    [statusSteps],
  );

  const episodeSteps = useMemo(
    () =>
      getEpisodeStatusSteps({ statusSteps: episodeStatusSteps }).sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [episodeStatusSteps],
  );

  const episodeLevel = useMemo(
    () =>
      episodeLevelStatusProgress({
        statusSteps: episodeSteps,
        statusCompletedIds: episodeStatusCompletedIds,
      }),
    [episodeSteps, episodeStatusCompletedIds],
  );

  const sceneRollup = useMemo(() => {
    let completed = 0;
    let total = 0;
    for (const scene of scenes) {
      const p = sceneStatusProgress(scene, statusSteps);
      completed += p.completed;
      total += p.total;
    }
    return {
      completed,
      total,
      missing: Math.max(0, total - completed),
    };
  }, [scenes, statusSteps]);

  const episodeTotals = useMemo(() => {
    const completed = sceneRollup.completed + episodeLevel.completed;
    const total = sceneRollup.total + episodeLevel.total;
    return {
      completed,
      total,
      pct: total === 0 ? 0 : Math.round((completed / total) * 100),
      duration: formatDuration(totalSceneDuration(scenes), {
        showHours,
        showFrames,
        fps,
      }),
    };
  }, [scenes, sceneRollup, episodeLevel, showHours, showFrames, fps]);

  const episodeOpen = expanded.has(EPISODE_EXPAND_ID);
  const expandedScenes = scenes.filter((s) => expanded.has(s.id));
  const stepSignature = [
    `episode:${sceneRollup.completed}:${sceneRollup.missing}:${episodeSteps
      .map((x) => `${x.id}:${x.label}`)
      .join("\0")}`,
    ...expandedScenes.map((s) =>
      getSceneStatusSteps(s, statusSteps)
        .map((x) => `${x.id}:${x.label}`)
        .join("\0"),
    ),
  ].join("|");
  const compactLabels = useCompactStatusLabels(sampleBarRef, stepSignature);

  const measureSceneId = useMemo(
    () => pickStatusMeasureSceneId(expandedScenes, statusSteps),
    [expandedScenes, statusSteps],
  );
  const measureEpisodeBar = episodeOpen && !measureSceneId;

  function toggleExpanded(id: Id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="sm-paper sm-paper-a4 sm-view-status">
      <DocumentHead
        productionTitle={productionTitle}
        seasonTitle={seasonTitle}
        episodeTitle={episodeTitle}
        view="status"
      />
      <EpisodeStatusStats
        sceneCount={scenes.length}
        durationLabel={episodeTotals.duration}
        completed={episodeTotals.completed}
        total={episodeTotals.total}
        pct={episodeTotals.pct}
      />

      {!readOnly ? (
        <div className="sm-status-toolbar">
          <button
            type="button"
            className={editing === "all" ? "is-active" : undefined}
            onClick={() => setEditing((cur) => (cur === "all" ? null : "all"))}
          >
            Standardpunkter
          </button>
          {onChangeEpisodeSteps ? (
            <button
              type="button"
              className={editing === "episode" ? "is-active" : undefined}
              onClick={() =>
                setEditing((cur) => (cur === "episode" ? null : "episode"))
              }
            >
              Episodepunkter
            </button>
          ) : null}
        </div>
      ) : null}

      {editing === "all" ? (
        <section className="sm-status-config" aria-label="Standardpunkter">
          <p className="sm-status-hint">
            Nye og fjernede punkter rammer alle scener. Rækkefølge og navne
            her gælder scener uden egne punkter — scener med egne lister
            beholder deres øvrige indstillinger.
          </p>
          <StepListEditor
            steps={productionList}
            readOnly={readOnly}
            onChange={onChangeProductionSteps}
          />
        </section>
      ) : null}

      {editing === "episode" && onChangeEpisodeSteps ? (
        <section className="sm-status-config" aria-label="Episodepunkter">
          <p className="sm-status-hint">
            Punkter for hele episoden (fx Lyd eller Grade). De vises mellem
            «Fuldførte scenepunkter» og «Manglende scenepunkter» og tæller i
            den samlede fremgang på samme måde som ét scenepunkt.
          </p>
          <StepListEditor
            steps={episodeSteps}
            readOnly={readOnly}
            onChange={onChangeEpisodeSteps}
          />
        </section>
      ) : null}

      <article
        className={`sm-status-episode-level${episodeOpen ? " is-open" : ""}`}
        onClick={() => toggleExpanded(EPISODE_EXPAND_ID)}
      >
        <div className="sm-status-scene-row">
          <button
            type="button"
            className="sm-status-expand"
            aria-expanded={episodeOpen}
            aria-label={episodeOpen ? "Skjul punkter" : "Vis punkter"}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(EPISODE_EXPAND_ID);
            }}
          >
            {episodeOpen ? "▾" : "▸"}
          </button>
          <div className="sm-status-scene-head">
            <span>Hele episoden</span>
            <span className="sm-status-pct">
              {episodeTotals.completed}/{episodeTotals.total} ·{" "}
              {episodeTotals.pct}%
            </span>
          </div>
          {!readOnly && onChangeEpisodeSteps ? (
            <button
              type="button"
              className={`sm-status-edit${
                editing === "episode" ? " is-active" : ""
              }`}
              title="Rediger episodepunkter"
              aria-pressed={editing === "episode"}
              onClick={(e) => {
                e.stopPropagation();
                setEditing((cur) => (cur === "episode" ? null : "episode"));
              }}
            >
              ✎
            </button>
          ) : null}
        </div>
        <div className="sm-status-progress" aria-hidden>
          <span style={{ width: `${episodeTotals.pct}%` }} />
        </div>
        {episodeOpen ? (
          <div
            className="sm-status-scene-body"
            onClick={(e) => e.stopPropagation()}
          >
            <EpisodeRollupBar
              sceneCompleted={sceneRollup.completed}
              sceneMissing={sceneRollup.missing}
              steps={episodeSteps}
              completedIds={episodeStatusCompletedIds}
              readOnly={readOnly || !onToggleEpisodeStep}
              barRef={measureEpisodeBar ? sampleBarRef : undefined}
              onToggle={(stepId) => onToggleEpisodeStep?.(stepId)}
              onReorder={(next) => onChangeEpisodeSteps?.(next)}
            />
          </div>
        ) : null}
      </article>

      <section className="sm-status-matrix" aria-label="Scenestatus">
        {scenes.map((scene) => {
          const sceneSteps = getSceneStatusSteps(scene, statusSteps);
          const { completed, total, pct } = sceneStatusProgress(
            scene,
            statusSteps,
          );
          const isOpen = expanded.has(scene.id);
          const isEditing = editing === scene.id;
          const heading = scene.heading?.trim() || scene.title || "Unavngiven";
          const directorClass = sceneRowClass?.(scene.id);
          return (
            <article
              key={scene.id}
              className={`sm-status-scene${isOpen ? " is-open" : ""}${
                directorClass ? ` ${directorClass}` : ""
              }`}
              style={{
                backgroundColor: sceneHeaderBg(
                  scene.headerBackground,
                  defaultSceneHeaderBg,
                ),
              }}
              onClick={() => {
                toggleExpanded(scene.id);
              }}
            >
              <div className="sm-status-scene-row">
                <button
                  type="button"
                  className="sm-status-expand"
                  aria-expanded={isOpen}
                  aria-label={isOpen ? "Skjul punkter" : "Vis punkter"}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(scene.id);
                  }}
                >
                  {isOpen ? "▾" : "▸"}
                </button>
                <div className="sm-status-scene-head">
                  <span>
                    Scene {scene.number}: {heading}
                  </span>
                  <span className="sm-status-pct">
                    {completed}/{total} · {pct}%
                  </span>
                </div>
                {!readOnly ? (
                  <button
                    type="button"
                    className={`sm-status-edit${isEditing ? " is-active" : ""}`}
                    title="Rediger punkter for denne scene"
                    aria-pressed={isEditing}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing((cur) => (cur === scene.id ? null : scene.id));
                    }}
                  >
                    ✎
                  </button>
                ) : null}
              </div>
              <div className="sm-status-progress" aria-hidden>
                <span style={{ width: `${pct}%` }} />
              </div>
              {isOpen ? (
                <div
                  className="sm-status-scene-body"
                  onClick={(e) => e.stopPropagation()}
                >
                <SceneStepBar
                  scene={scene}
                  steps={sceneSteps}
                  compactLabels={compactLabels}
                  readOnly={readOnly}
                  barRef={
                    scene.id === measureSceneId ? sampleBarRef : undefined
                  }
                  onToggle={(stepId) => onToggleStep(scene.id, stepId)}
                  onReorder={(next) => onChangeSceneSteps(scene.id, next)}
                />
                </div>
              ) : null}
              {isEditing ? (
                <section
                  className="sm-status-scene-editor"
                  aria-label={`Punkter for scene ${scene.number}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="sm-status-hint">
                    Gælder kun denne scene. Tomt felt = mellemrum. Træk i
                    ⋮⋮ for at flytte.
                  </p>
                  <StepListEditor
                    steps={sceneSteps}
                    readOnly={readOnly}
                    onChange={(next) => onChangeSceneSteps(scene.id, next)}
                  />
                  {scene.statusSteps && scene.statusSteps.length > 0 ? (
                    <button
                      type="button"
                      className="sm-status-reset"
                      disabled={readOnly}
                      onClick={() => onChangeSceneSteps(scene.id, null)}
                    >
                      Brug standardpunkter
                    </button>
                  ) : null}
                </section>
              ) : null}
            </article>
          );
        })}
      </section>
    </div>
  );
}

function PrintStatusStepBar({
  scene,
  steps,
  compactLabels,
  barRef,
}: {
  scene: Scene;
  steps: StatusStepDef[];
  compactLabels: boolean;
  barRef?: RefObject<HTMLDivElement | null>;
}) {
  const done = new Set(scene.statusCompletedIds ?? []);
  return (
    <div
      ref={(el) => {
        if (barRef) barRef.current = el;
      }}
      className={`sm-status-bar${compactLabels ? " is-compact" : ""}`}
      role="group"
      aria-label={`Status for scene ${scene.number}`}
    >
      {steps.map((step) => {
        const checked = done.has(step.id);
        const blank = isStatusStepSpacer(step);
        return (
          <div
            key={step.id}
            className={`sm-status-step${checked ? " is-done" : ""}${
              blank ? " is-spacer" : ""
            }`}
            aria-label={
              blank
                ? undefined
                : `${step.label}${checked ? ", færdig" : ""}`
            }
          >
            <span className="sm-status-step-mark">
              {blank ? "" : checked ? "✓" : ""}
            </span>
            <span className="sm-status-step-label">{step.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Working-view status cards for Forbered udskrift / PDF. */
export function StatusPrintScenes({
  scenes,
  statusSteps,
  episodeStatusSteps = [],
  episodeStatusCompletedIds = [],
  defaultSceneHeaderBg,
  expanded,
  pageBreakBeforeScene,
  onCompactLabelsChange,
  sceneRowClass,
}: {
  scenes: Scene[];
  statusSteps: StatusStepDef[];
  episodeStatusSteps?: StatusStepDef[];
  episodeStatusCompletedIds?: Id[];
  defaultSceneHeaderBg: string;
  expanded: boolean;
  pageBreakBeforeScene?: boolean;
  onCompactLabelsChange?: (compact: boolean) => void;
  sceneRowClass?: (sceneId: Id) => string | undefined;
}) {
  const sampleBarRef = useRef<HTMLDivElement | null>(null);
  const episodeSteps = useMemo(
    () => getEpisodeStatusSteps({ statusSteps: episodeStatusSteps }),
    [episodeStatusSteps],
  );
  const episodeLevel = useMemo(
    () =>
      episodeLevelStatusProgress({
        statusSteps: episodeSteps,
        statusCompletedIds: episodeStatusCompletedIds,
      }),
    [episodeSteps, episodeStatusCompletedIds],
  );
  const sceneRollup = useMemo(() => {
    let completed = 0;
    let total = 0;
    for (const scene of scenes) {
      const p = sceneStatusProgress(scene, statusSteps);
      completed += p.completed;
      total += p.total;
    }
    return {
      completed,
      total,
      missing: Math.max(0, total - completed),
    };
  }, [scenes, statusSteps]);
  const episodeTotals = useMemo(() => {
    const completed = sceneRollup.completed + episodeLevel.completed;
    const total = sceneRollup.total + episodeLevel.total;
    return {
      completed,
      total,
      pct: total === 0 ? 0 : Math.round((completed / total) * 100),
    };
  }, [sceneRollup, episodeLevel]);
  const printScenes = expanded ? scenes : [];
  const stepSignature = [
    `episode:${sceneRollup.completed}:${sceneRollup.missing}:${episodeSteps
      .map((x) => `${x.id}:${x.label}`)
      .join("\0")}`,
    ...printScenes.map((s) =>
      getSceneStatusSteps(s, statusSteps)
        .map((x) => `${x.id}:${x.label}`)
        .join("\0"),
    ),
  ].join("|");
  const compactLabels = useCompactStatusLabels(
    sampleBarRef,
    expanded ? stepSignature : "",
  );
  const measureSceneId = useMemo(
    () =>
      expanded ? pickStatusMeasureSceneId(scenes, statusSteps) : null,
    [expanded, scenes, statusSteps],
  );
  const measureEpisodeBar = expanded && !measureSceneId;

  useLayoutEffect(() => {
    onCompactLabelsChange?.(expanded && compactLabels);
  }, [expanded, compactLabels, onCompactLabelsChange]);

  return (
    <>
      <article
        className={`sm-status-episode-level${expanded ? " is-open" : ""}`}
      >
        <div className="sm-status-scene-row">
          <div className="sm-status-scene-head">
            <span>Hele episoden</span>
            <span className="sm-status-pct">
              {episodeTotals.completed}/{episodeTotals.total} ·{" "}
              {episodeTotals.pct}%
            </span>
          </div>
        </div>
        <div className="sm-status-progress" aria-hidden>
          <span style={{ width: `${episodeTotals.pct}%` }} />
        </div>
        {expanded ? (
          <div className="sm-status-scene-body">
            <PrintEpisodeRollupBar
              sceneCompleted={sceneRollup.completed}
              sceneMissing={sceneRollup.missing}
              steps={episodeSteps}
              completedIds={episodeStatusCompletedIds}
              barRef={measureEpisodeBar ? sampleBarRef : undefined}
            />
          </div>
        ) : null}
      </article>
      <section className="sm-status-matrix" aria-label="Scenestatus">
        {scenes.map((scene, sceneIndex) => {
          const sceneSteps = getSceneStatusSteps(scene, statusSteps);
          const { completed, total, pct } = sceneStatusProgress(
            scene,
            statusSteps,
          );
          const heading = scene.heading?.trim() || scene.title || "Unavngiven";
          const directorClass = sceneRowClass?.(scene.id);
          return (
            <article
              key={scene.id}
              className={`sm-status-scene${expanded ? " is-open" : ""}${
                pageBreakBeforeScene && sceneIndex > 0
                  ? " sm-print-break-before"
                  : ""
              }${directorClass ? ` ${directorClass}` : ""}`}
              style={{
                backgroundColor: sceneHeaderBg(
                  scene.headerBackground,
                  defaultSceneHeaderBg,
                ),
              }}
            >
              <div className="sm-status-scene-row">
                <div className="sm-status-scene-head">
                  <span>
                    Scene {scene.number}: {heading}
                  </span>
                  <span className="sm-status-pct">
                    {completed}/{total} · {pct}%
                  </span>
                </div>
              </div>
              <div className="sm-status-progress" aria-hidden>
                <span style={{ width: `${pct}%` }} />
              </div>
              {expanded ? (
                <div className="sm-status-scene-body">
                  <PrintStatusStepBar
                    scene={scene}
                    steps={sceneSteps}
                    compactLabels={compactLabels}
                    barRef={
                      scene.id === measureSceneId ? sampleBarRef : undefined
                    }
                  />
                </div>
              ) : null}
            </article>
          );
        })}
      </section>
    </>
  );
}

function PrintEpisodeRollupBar({
  sceneCompleted,
  sceneMissing,
  steps,
  completedIds,
  barRef,
}: {
  sceneCompleted: number;
  sceneMissing: number;
  steps: StatusStepDef[];
  completedIds: Id[];
  barRef?: RefObject<HTMLDivElement | null>;
}) {
  const done = new Set(completedIds);
  const localRef = useRef<HTMLDivElement | null>(null);
  const customs = useMemo(
    () =>
      steps.map((s) => ({
        label: s.label,
        done: done.has(s.id),
        blank: isStatusStepSpacer(s),
      })),
    [steps, completedIds],
  );
  const widths = useEpisodeRollupWidths(
    localRef,
    sceneCompleted,
    sceneMissing,
    customs,
  );
  return (
    <div
      ref={(el) => {
        localRef.current = el;
        if (barRef) barRef.current = el;
      }}
      className="sm-status-bar sm-status-bar-episode"
      role="group"
      aria-label="Status for hele episoden"
    >
      {widths.fuldførte != null ? (
        <div
          className="sm-status-step is-rollup is-done"
          style={{
            flex: `0 0 ${Math.max(0, widths.fuldførte)}px`,
            width: Math.max(0, widths.fuldførte),
            maxWidth: Math.max(0, widths.fuldførte),
          }}
        >
          <span className="sm-status-step-mark">✓</span>
          <span className="sm-status-step-label">Fuldførte scenepunkter</span>
          <span className="sm-status-step-count">{sceneCompleted}</span>
        </div>
      ) : null}
      {steps.map((step, index) => {
        const checked = done.has(step.id);
        const blank = isStatusStepSpacer(step);
        const w = widths.customs[index] ?? 0;
        const horizontal = blank || !!widths.customHorizontal[index];
        return (
          <div
            key={step.id}
            className={`sm-status-step${checked ? " is-done" : ""}${
              blank ? " is-spacer" : ""
            }${horizontal ? "" : " is-label-vertical"}`}
            style={{
              flex: `0 0 ${w}px`,
              width: w,
              maxWidth: w,
            }}
            aria-label={
              blank
                ? undefined
                : `${step.label}${checked ? ", færdig" : ""}`
            }
          >
            <span className="sm-status-step-mark">
              {blank ? "" : checked ? "✓" : ""}
            </span>
            <span className="sm-status-step-label">{step.label}</span>
          </div>
        );
      })}
      {widths.manglende != null ? (
        <div
          className="sm-status-step is-rollup is-missing"
          style={{
            flex: `0 0 ${Math.max(0, widths.manglende)}px`,
            width: Math.max(0, widths.manglende),
            maxWidth: Math.max(0, widths.manglende),
          }}
        >
          <span className="sm-status-step-mark" />
          <span className="sm-status-step-label">Manglende scenepunkter</span>
          <span className="sm-status-step-count">{sceneMissing}</span>
        </div>
      ) : null}
    </div>
  );
}
